/**
 * The brief is pasted as markdown and drawn as HTML.
 *
 * A hand-written renderer rather than a markdown library, and that is a
 * deliberate trade rather than a shortcut. What gets pasted here is one shape:
 * the output of an AI writing a daily operator brief. Headings, bold, bullets,
 * numbers, links, the odd rule. A full CommonMark implementation would bring
 * reference links, HTML passthrough, setext headings and footnotes, none of
 * which appear, and all of which are surface for something to render wrong on
 * the CEO's phone.
 *
 * Everything is escaped FIRST and only the marks below are turned back into
 * tags, so a brief that quotes an angle bracket cannot inject anything. The
 * output is then run through the same DOMPurify pass the rest of the Hub uses
 * before it reaches the page.
 */

export interface BriefItem {
  html: string;
  /** Sub-bullets, one level deep. Rendered inline HTML. */
  children: string[];
}

export interface BriefBlock {
  kind: 'h1' | 'h2' | 'h3' | 'p' | 'ul' | 'ol' | 'rule';
  /** The rendered inline HTML. For lists, one entry per TOP-LEVEL item. */
  html: string[];
  /** Lists only: the items with their sub-bullets. Same order as html. */
  items?: BriefItem[];
}

/** Escaped first, always. Nothing below can reintroduce a raw tag. */
export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Inline marks, applied to already-escaped text.
 *
 * Order matters: bold before italic, or `**a**` leaves a stray asterisk pair.
 * Links are last so their text can carry the other marks.
 */
export function renderInline(raw: string): string {
  let out = escapeHtml(raw);

  /*
   * Code spans are lifted out before anything else runs and put back last.
   *
   * Without this, `next_action_date` renders as next<em>action</em>date: the
   * underscore rule does not know it is inside code. Column and function names
   * are exactly what a brief about the Hub contains, so this is the common
   * case rather than an edge one. Caught by a test, not by reading.
   */
  const codes: string[] = [];
  out = out.replace(/`([^`]+)`/g, (_match, code: string) => {
    codes.push(code);
    return `\uE000CODE${String(codes.length - 1)}\uE000`;
  });

  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  out = out.replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>');
  out = out.replace(/(^|[^_])_([^_]+)_/g, '$1<em>$2</em>');
  // Only http and https. A markdown link is the one place a paste could carry
  // javascript: into an href, and the brief has no need for any other scheme.
  out = out.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
    '<a href="$2" target="_blank" rel="noreferrer noopener">$1</a>',
  );

  return out.replace(/\uE000CODE(\d+)\uE000/g, (_match, index: string) => {
    const code = codes[Number(index)] ?? '';
    return `<code>${code}</code>`;
  });
}

const H = /^(#{1,3})\s+(.*)$/;
/** -, *, •, plus the glyphs a chat app pastes for sub-bullets: ◦ ▪ – */
const BULLET = /^(\s*)[-*•◦▪–]\s+(.*)$/;
const NUMBER = /^(\s*)\d+[.)]\s+(.*)$/;
const RULE = /^\s*([-*_])\1{2,}\s*$/;
/** "**Signals:**" — a bold label ending in a colon, alone or opening a line. */
const LABEL_START = /^(\*\*|__)[^*_]+?:\s*\1/;
const LABEL_ONLY = /^(\*\*|__)[^*_]+?:\s*\1\s*$/;
/** "**Dev 1**" or "**Admin / CEO Support:**" on a line of its own. */
const BOLD_LINE = /^(\*\*|__)([^*_]{1,80}?)\1[\s:-]*$/;

/** Two spaces or a tab under a bullet means "under it". */
function indentOf(whitespace: string): number {
  return whitespace.replace(/\t/g, '    ').length;
}

/**
 * "Dev 2 -" and "Signals:" read as headings; the dash and colon were the
 * writer's way of saying so and have no job once the type says it.
 */
function trimHeadingText(text: string): string {
  return text.replace(/[\s:-]+$/, '').trim();
}

interface RawItem {
  text: string;
  children: string[];
}

/**
 * Markdown to blocks.
 *
 * Blank lines separate blocks; consecutive list items of the same kind join
 * into one list. A wrapped paragraph rejoins with a space rather than a break,
 * because an AI writing prose wraps where its own line length ran out and not
 * where a line break was meant.
 *
 * ── The tidy-up ────────────────────────────────────────────────────────────
 *
 * Kyu, 11 September, looking at a pasted brief: "auto refine the formatting
 * spacing and maybe add some lines in between sections". The AI that writes
 * the brief flattens its structure into one long bullet list: "**Signals:**"
 * as a bullet, then the signals as bullets beside it, then "**Next move:**",
 * and so on. On a phone that is a wall of dots. So the parser reads the
 * shape the writer meant:
 *
 * - An indented bullet is a sub-bullet of the one above it.
 * - A bullet that is ONLY a bold label ending in a colon opens a group; the
 *   plain bullets after it are its sub-bullets until the next label. A
 *   bullet that starts with a bold label AND carries text ("**Pulse:** The
 *   CEO Brief moved…") is a normal top-level item, never swallowed.
 * - A paragraph that is one short bold line ("**Dev 1**") is a heading.
 *
 * Nothing is dropped and nothing is reordered: a brief that already nests
 * properly comes out the same.
 */
export function parseBrief(markdown: string): BriefBlock[] {
  const blocks: BriefBlock[] = [];
  let paragraph: string[] = [];
  let list: { kind: 'ul' | 'ol'; items: RawItem[] } | null = null;

  function flushParagraph() {
    if (paragraph.length === 0) return;
    const only = paragraph.length === 1 ? BOLD_LINE.exec(paragraph[0] ?? '') : null;
    if (only !== null) {
      blocks.push({ kind: 'h3', html: [renderInline(trimHeadingText(only[2] ?? ''))] });
    } else {
      blocks.push({ kind: 'p', html: [renderInline(paragraph.join(' '))] });
    }
    paragraph = [];
  }
  function flushList() {
    if (list === null) return;
    const items = list.items.map((item) => ({
      html: renderInline(item.text),
      children: item.children.map(renderInline),
    }));
    blocks.push({ kind: list.kind, html: items.map((item) => item.html), items });
    list = null;
  }
  function addItem(kind: 'ul' | 'ol', indent: number, text: string) {
    flushParagraph();
    const last = list?.items[list.items.length - 1];
    if (list !== null && last !== undefined) {
      // Under the item above: said with indentation, or with a label bullet
      // that this plain bullet belongs to.
      const underLabel = LABEL_ONLY.test(last.text) && !LABEL_START.test(text);
      if (indent >= 2 || underLabel) {
        last.children.push(text);
        return;
      }
    }
    if (list?.kind !== kind) {
      flushList();
      list = { kind, items: [] };
    }
    list.items.push({ text, children: [] });
  }
  function flushAll() {
    flushParagraph();
    flushList();
  }

  for (const line of markdown.replace(/\r\n?/g, '\n').split('\n')) {
    if (line.trim() === '') {
      flushAll();
      continue;
    }
    if (RULE.test(line)) {
      flushAll();
      blocks.push({ kind: 'rule', html: [] });
      continue;
    }
    const heading = H.exec(line);
    if (heading !== null) {
      flushAll();
      const level = heading[1]?.length ?? 1;
      blocks.push({
        kind: level === 1 ? 'h1' : level === 2 ? 'h2' : 'h3',
        html: [renderInline(heading[2] ?? '')],
      });
      continue;
    }
    const bullet = BULLET.exec(line);
    if (bullet !== null) {
      // The kind only matters for a NEW top-level item: an indented bullet
      // nests under whatever is above it, numbered or not.
      addItem('ul', indentOf(bullet[1] ?? ''), bullet[2] ?? '');
      continue;
    }
    const numbered = NUMBER.exec(line);
    if (numbered !== null) {
      addItem('ol', indentOf(numbered[1] ?? ''), numbered[2] ?? '');
      continue;
    }
    flushList();
    paragraph.push(line.trim());
  }
  flushAll();
  return blocks;
}

/** "Thursday, 11 September" — how Alex reads a date, not how a database does. */
export function briefDateLabel(isoDate: string): string {
  const parsed = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  // Built from parts rather than one toLocaleDateString call: en-GB renders
  // "Friday 11 September" with no comma, and the comma is what makes it read
  // as a date line rather than a run-on.
  const weekday = parsed.toLocaleDateString('en-GB', { weekday: 'long' });
  const rest = parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
  return `${weekday}, ${rest}`;
}

/** "11 Sept" for the archive list, with the year only when it is not this one. */
export function briefShortDate(isoDate: string, today: Date): string {
  const parsed = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  const sameYear = parsed.getFullYear() === today.getFullYear();
  return parsed.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

export interface BriefSection {
  /** Null for anything above the first heading. */
  heading: BriefBlock | null;
  body: BriefBlock[];
}

const HEADINGS = new Set(['h1', 'h2', 'h3']);

/**
 * Blocks grouped under their headings, so the page can indent everything
 * that belongs to a title one step in from the title itself.
 *
 * Kyu, 11 September: "After a section title, all the lists or items below
 * it should have like one space apart to the right." Every heading opens a
 * section; every non-heading block joins the open one. Rules stay in the
 * body, so a `---` the writer put between two parts stays where it was.
 */
export function groupBriefBlocks(blocks: readonly BriefBlock[]): BriefSection[] {
  const sections: BriefSection[] = [];
  let open: BriefSection = { heading: null, body: [] };
  for (const block of blocks) {
    if (HEADINGS.has(block.kind)) {
      if (open.heading !== null || open.body.length > 0) sections.push(open);
      open = { heading: block, body: [] };
      continue;
    }
    open.body.push(block);
  }
  if (open.heading !== null || open.body.length > 0) sections.push(open);
  return sections;
}
